import some from "lodash/some";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { FetchMessagesResponse } from "PFCore/services/chat/fetch_messages";
import { Dispatch, SetStateAction, useEffect } from "react";

const FLASH_TITLE_DURATION_MS = 3000;
const DEBOUNCE_MS = 200;

type PusherChatMessageEvent = { id: number | string };

type UseFlashTitleOnNewMessageParams = {
  lastReceivedMessage: PusherChatMessageEvent | null;
  messages?: { pages: FetchMessagesResponse[]; pageParams: number[] };
  minimized?: boolean;
  refetchConversation: () => void;
  refetchMessages: () => void;
  setFlashTitle: Dispatch<SetStateAction<boolean>>;
  setLastReceivedMessage: Dispatch<SetStateAction<PusherChatMessageEvent | null>>;
};

const isUnseenMessage = (
  lastReceivedMessage: PusherChatMessageEvent | null,
  messages?: { pages: FetchMessagesResponse[]; pageParams: number[] }
) => {
  const entries = messages?.pages?.[0]?.entries;
  return !!lastReceivedMessage && !some(entries, { id: lastReceivedMessage.id });
};

const flashTitleBriefly = (setFlashTitle: Dispatch<SetStateAction<boolean>>) => {
  setFlashTitle(true);
  setTimeout(() => setFlashTitle(false), FLASH_TITLE_DURATION_MS);
};

// PusherWrapper is binding the lexical scope so we need to set the lastReceivedMessage in state
// In order to keep in sync with query update we need to debounce the flashTitle update and refetch
export const useFlashTitleOnNewMessage = ({
  lastReceivedMessage,
  messages,
  minimized,
  refetchConversation,
  refetchMessages,
  setFlashTitle,
  setLastReceivedMessage
}: UseFlashTitleOnNewMessageParams) => {
  const handleNewMessage = () => {
    if (isUnseenMessage(lastReceivedMessage, messages)) {
      // if chat is minimized we just want to increase unread count (refetch conversation)
      minimized ? refetchConversation() : refetchMessages();
      flashTitleBriefly(setFlashTitle);
    }
    setLastReceivedMessage(null);
  };

  useEffect(useDebounce(handleNewMessage, DEBOUNCE_MS), [lastReceivedMessage]);
};
